import React, { useRef, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  Divider,
} from "@mui/material";

import CheckMarkIcon from "../../../assets/icons/CheckMark.svg";


interface SidebarProps {
  collapsed: boolean;
  setCollapsed: (value: boolean) => void;
  selectedOptions: string[];
  setSelectedOptions: React.Dispatch<React.SetStateAction<string[]>>;
}

const Sidebar: React.FC<SidebarProps> = ({
  collapsed,
  setCollapsed,
  selectedOptions,
  setSelectedOptions,
}) => {
  const [mode, setMode] = React.useState("stations");
  const [searchTerm, setSearchTerm] = React.useState("");
  const [dropdownVisible, setDropdownVisible] = React.useState(false);
  const wrapperRef = useRef(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        wrapperRef.current &&
        !(wrapperRef.current as HTMLElement).contains(event.target as Node)
      ) {
        setDropdownVisible(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const stationOptions = [
    "View Available Stations",
    "Total Operational Stations",
    "View 'In Progress' Stations",
    "View Faulted Stations",
    "View Most Recently Added Stations",
    "Total Stations Flagged for Review",
  ];

  const equipmentOptions = [
    "View All Chargers",
    "View All Ports",
    "View All Connectors",
    "View All Battery Energy Storage Systems",
    "View Most Recently Added Equipment",
  ];

  const currentOptions = mode === "stations" ? stationOptions : equipmentOptions;
  const title = mode === "stations" ? "Stations" : "Equipment";

  const filteredOptions = currentOptions.filter((option) =>
    option.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleToggleMode = (newMode: string) => {
    setMode(newMode);
    setSelectedOptions([]);
    setSearchTerm("");
    setDropdownVisible(false);
  };

  const handleOptionClick = (option: string) => {
    setSelectedOptions((prev) =>
      prev.includes(option)
        ? prev.filter((item) => item !== option)
        : [...prev, option]
    );
    setSearchTerm("");
  };

  const handleViewAll = () => {
    setSelectedOptions(currentOptions);
  };

  const handleClear = () => {
    setSelectedOptions([]);
    setSearchTerm("");
  };

  return (
    <Box
      sx={{
        position: "relative",
        display: "inline-block",
      }}
    >
      {!collapsed && (
        <Box
          ref={wrapperRef}
          sx={{
            backgroundColor: "#e0e0e0",
            padding: "5px",
            width: "310px",
            transition: "width 0.3s",
          }}
        >
          <Paper
            elevation={0}
            sx={{
              width: "100%",
              fontFamily: "Arial, sans-serif",
              backgroundColor: "#fff",
              p: 0,
              overflow: "hidden",
              boxShadow: "none",
            }}
          >
            <Box display="flex">
              {["stations", "equipment"].map((label) => (
                <Button
                  key={label}
                  onClick={() => handleToggleMode(label)}
                  sx={{
                    flex: 1,
                    borderRadius: 0,
                    py: 1.5,
                    backgroundColor:
                      mode === label ? "var(--terbine-blue)" : "#555",
                    color: "#fff",
                    fontWeight: "bold",
                    "&:hover": {
                      backgroundColor:
                        mode === label ? "var(--terbine-blue)" : "#444",
                    },
                  }}
                >
                  {label.charAt(0).toUpperCase() + label.slice(1)}
                </Button>
              ))}
            </Box>

            <Box px={2} pt={2}>
              <Typography
                variant="h6"
                fontWeight="bold"
                textAlign="center"
                mb={1}
              >
                {title}
              </Typography>

              <Box display="flex" justifyContent="space-between" mb={1}>
                <Box display="flex" gap={1}>
                  <Button size="small" onClick={handleViewAll}>
                    View All
                  </Button>
                  <Button size="small" onClick={handleClear}>
                    Clear
                  </Button>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  Displaying {filteredOptions.length}
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  flexWrap: "nowrap",
                  alignItems: "center",
                  gap: 0.5,
                  backgroundColor: "#fff",
                  px: 1,
                  py: 0.5,
                  border: "1px solid #ccc",
                  borderRadius: 1,
                  minHeight: 40,
                  maxWidth: "100%",
                  overflowX: "auto",
                  whiteSpace: "nowrap",
                  scrollbarWidth: "none",
                  "&::-webkit-scrollbar": {
                    display: "none",
                  },
                  cursor: "text",
                }}
                onClick={() => {
                  setDropdownVisible(true);
                  inputRef.current?.focus();
                }}
              >
                {selectedOptions.map((option) => (
                  <Box
                    key={option}
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      backgroundColor: "#e0e0e0",
                      color: "#000",
                      px: 1.5,
                      py: 0.5,
                      borderRadius: "16px",
                      fontSize: "0.75rem",
                      fontWeight: 500,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {option}
                  </Box>
                ))}

                <input
                  ref={inputRef}
                  type="text"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setDropdownVisible(true);
                  }}
                  onFocus={() => setDropdownVisible(true)}
                  style={{
                    flexGrow: 1,
                    minWidth: 60,
                    border: "none",
                    outline: "none",
                    fontSize: "0.875rem",
                    padding: "4px 0",
                    whiteSpace: "nowrap",
                  }}
                  placeholder={selectedOptions.length === 0 ? "Search options..." : ""}
                />
              </Box>
            </Box>

            <Box
              px={2}
              pb={2}
              pt={1}
              sx={{
                minHeight: 240,
                transition: "opacity 0.2s",
              }}
            >
              <Divider sx={{ mb: 1 }} />
              <Box
                display="flex"
                flexDirection="column"
                gap={1}
                sx={{
                  opacity: dropdownVisible ? 1 : 0,
                  pointerEvents: dropdownVisible ? "auto" : "none",
                  transition: "opacity 0.2s",
                }}
              >
                {filteredOptions.map((option, index) => {
                  const isSelected = selectedOptions.includes(option);
                  return (
                    <Box
                      key={index}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        handleOptionClick(option);
                      }}
                      sx={{
                        width: "100%",
                        backgroundColor: isSelected ? "var(--terbine-blue)" : "transparent",
                        color: isSelected ? "#fff" : "#000",
                        fontWeight: isSelected ? "bold" : "normal",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        "&:hover": {
                          backgroundColor: isSelected ? "var(--terbine-blue)" : "#f0f0f0",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          px: 2,
                          py: 1,
                          display: "flex",
                          alignItems: "center",
                          width: "100%",
                        }}
                      >
                        {isSelected && (
                          <img
                            src={CheckMarkIcon}
                            alt="Selected"
                            style={{ width: 16, height: 16, marginRight: 8 }}
                          />
                        )}
                        {option}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Paper>
        </Box>
      )}
    </Box>
  );
};

export default Sidebar;